// React imports
import React, { useState, useEffect, useMemo } from 'react'

// General imports
import _ from 'lodash';
import dayjs from 'dayjs';
import axios from 'axios';
import { useParams, useOutletContext } from 'react-router-dom';
import pluralize from 'pluralize';
import oxfordJoin from 'oxford-join';

// Font Awesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRightFromLine, faCircleXmark, faShip, faPlane, faCity, faArrowLeft, faSquare, faSquareCheck, faMagnifyingGlass, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { faCheckSquare } from '@fortawesome/pro-solid-svg-icons';

// Mui imports
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';

// Local imports
import SortButton from '../components/SortButton.js';
import Where from '../../Where.js';
import TransportationMethod from '../../TransportationMethod.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import { calcWeightOrMeasurment, calcWeight, calcChargeableWeight, calcTotalVolume } from '../../CalcWeight.js';
import { calcOfferSum } from '../../utils/opportunity.js';
import RatesDetails from '../../RatesDetails.js';
import { RateCard, shouldRenderRateCard } from '../../RateCard.js';
import OKARGO_PLATFORMS from '../../OKARGO_PLATFORMS.json';
import { createRateProduct, migrateRateToLCL, migrateRateToAirFreight, migrateWMRatePairs } from '../utils/product.js';
import FiltersPanel from './FiltersPanel.js';
import SortingPanel from './SortingPanel.js';
let db = null;

// A selected location's icon should reflect what the location IS, not the search method.
// Sea ports and airports are both stored as type 'port' (a sea port's id is a 5-char UN/LOCODE
// like "AUBNE"; an airport's is a 3-letter IATA like "BNE"), so keying the icon off the
// transportation method wrongly showed a plane for a sea port on a Land/door search.
function locationIcon(loc) {
    if (!loc || loc.type !== 'port') return faCity;
    return /^[A-Z]{3}$/.test(loc.id || '') ? faPlane : faShip;
}

function SearchPane(props) {
    const context = useOutletContext();
    const { setMinimizedSearch, seaPorts, unlocode, accesstoken, org, exchangeRates, opportunities, _axios = axios, fullscreen = false } = _.isUndefined(props.minimizedSearch) ? { ...context, fullscreen: true}  : props;
    let { opportunityId, emailId } = useParams();

    const [ page, setPage ] = useState(0);
    const [ searches, setSearches ] = useState({});
    const [ showSearchResult, setShowSearchResult ] = useState(false);
    const [ activeSearch, setActiveSearch ] = useState(-1);
    const [ activeRates, setActiveRates ] = useState([]);
    const [ sorting, setSorting ] = useState('price-asc');
    const [ filters, setFilters ] = useState([]);
    const [ modeFilter, setModeFilter ] = useState('rates');
    const [ hideSections, setHideSections ] = useState([]);
    const [ loadFromOpportunity, setLoadFromOpportunity ] = useState(null);
    const [ lastSearchLCLProduct, setLastSearchLCLProduct ] = useState(null);
    const [ lastSearchAirFreightProduct, setLastSearchAirFreightProduct ] = useState(null);
    const [ toggleReset, setToggleReset ] = useState(false);
    const [ loadingFromOpportunity, setLoadingFromOpportunity ] = useState(false);
    const [ errorLoadingPorts, setErrorLoadingPorts ] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        (async () => {
            if (!org?.alias) return;
            try {
                db = await getDB({ orgAlias: org.alias });

                const transaction = db.transaction(['searches'], 'readwrite');
                
                // Add transaction-level error handling
                transaction.onerror = (event) => {
                    console.error('[SearchPane] Transaction error loading history:', event.target.error);
                };
                transaction.onabort = (event) => {
                    console.error('[SearchPane] Transaction aborted loading history:', event.target.error);
                };
                
                const searchesStore = transaction.objectStore('searches');

                const _searches = await new Promise(res => {
                    const request = searchesStore.getAll();
                    request.onsuccess = (event) => res(event.target.result);
                    request.onerror = (event) => { console.error('[SearchPane] Error loading searches', event.target.error); res([]); };
                });

                if (_searches.length !== 0) {
                    onLoadHistory();
                }
            } catch (e) {
                console.error('[SearchPane] Error getting db', e);
            }
        })();   
    }, [ org?.alias ]);

    useEffect(() => {
        if (db) {
            try {
                const transaction = db.transaction(['searches'], 'readwrite');
                
                // Add transaction-level error handling
                transaction.onerror = (e) => {
                    console.error('[SearchPane] Transaction error saving history:', e.target?.error || e);
                };
                transaction.onabort = (e) => {
                    console.error('[SearchPane] Transaction aborted saving history:', e.target?.error || e);
                };
                
                transaction.oncomplete = () => {
                    console.log('[SearchPane] Searches saved');
                };

                const searchesStore = transaction.objectStore('searches');
                _.values(searches).forEach(s => {
                    const request = searchesStore.put(s);
                    request.onerror = (e) => console.error('[SearchPane] Error saving search', e.target?.error || e);
                });

            } catch (e) {
                console.error('[SearchPane] Error saving history', e);
            }
        }
    }, [searches, org?.id]);

    // Reset state when switching tabs
    useEffect(() => {
        // When returning to history tab, reset to overview
        if (page === 1) {
            setActiveSearch(-1);
            setActiveRates([]);
        }
        // When returning to search tab, reset to search form (not results)
        if (page === 0) {
            setShowSearchResult(false);
        }
    }, [page]);
    window.searches = searches;
    window.activeSearch = activeSearch;
    window.activeRates = activeRates;

    async function onLoadHistory() {
        if (!db) {
            console.warn('[SearchPane] Cannot load history - database not available');
            return;
        }
        
        try {
            const transaction = db.transaction(['searches'], 'readwrite');
            
            // Add transaction-level error handling
            transaction.onerror = (event) => {
                console.error('[SearchPane] Transaction error loading history:', event.target?.error || event);
            };
            transaction.onabort = (event) => {
                console.error('[SearchPane] Transaction aborted loading history:', event.target?.error || event);
            };
            
            const searchesStore = transaction.objectStore('searches');

            const _searches = await new Promise(res => {
                const request = searchesStore.getAll();
                request.onsuccess = (event) => res(event.target.result);
                request.onerror = (event) => { console.error('[SearchPane] Error loading searches', event.target.error); res([]); };
            });

            console.log('[SearchPane] Searches', _searches);

            setSearches(_.mapKeys(_searches, s => s.created));
            setPage(0);
        } catch (e) {
            console.error('[SearchPane] Error in onLoadHistory:', e);
        }
    }

    async function onClearHistory() {
        if (!db) {
            console.warn('[SearchPane] Cannot clear history - database not available');
            setSearches({});
            return;
        }
        
        try {
            const transaction = db.transaction(['searches'], 'readwrite');
            
            // Add transaction-level error handling
            transaction.onerror = (event) => {
                console.error('[SearchPane] Transaction error clearing history:', event.target?.error || event);
            };
            transaction.onabort = (event) => {
                console.error('[SearchPane] Transaction aborted clearing history:', event.target?.error || event);
            };
            
            const searchesStore = transaction.objectStore('searches');

            await new Promise((res, rej) => {
                const request1 = searchesStore.clear();
                request1.onsuccess = () => {
                    res();
                };
                request1.onerror = (e) => rej(e.target.error);
            });

            setSearches({});
        } catch (e) {
            console.error('[SearchPane] Error clearing history:', e);
            // Still clear the UI state even if IndexedDB clear fails
            setSearches({});
        }
    }

    function onRetry({ platform }) {
        const search = page === 0 ? _.maxBy(_.values(searches), s => s.created) : page === 1 ? searches[activeSearch] : null;
        const prevSearch = {
            ...search,
            platforms: [
                ...search.platforms.filter(p => p.id !== platform.id), 
                search.platforms.find(p => p.id === platform.id)
            ],
            donePlatforms: search.donePlatforms.filter(p => p.id !== platform.id),
            results: search.results.filter(result => result.platform !== platform.id),
        };

        onSearch({ 
            sources: search.sources,
            destinations: search.destinations,
            transportationMethod: search.transportationMethod,
            mode: search.mode,
            portals: search.portals,
            products: search.products,
            dangerous: search.dangerous,
            chargeableWeight: search.chargeableWeight,
            chargeableWeightQuantity: search.chargeableWeightQuantity,
            chargeableWeightGrossWeight: search.chargeableWeightGrossWeight,
            weightOrMeasurement: search.weightOrMeasurement,
            dow: search.dow,
            volumeWeightVolume: search.volumeWeightVolume,
            volumeWeightWeight: search.volumeWeightWeight,
            volumeWeightQuantity: search.volumeWeightQuantity,
            prevSearch
        });
    }

    async function onSearch({ sources, destinations, transportationMethod, mode, portals, products, dangerous, chargeableWeight, chargeableWeightQuantity, chargeableWeightGrossWeight, dow, volumeWeightVolume, volumeWeightWeight, volumeWeightQuantity, weightOrMeasurement, prevSearch }) {

        setShowSearchResult(true);
        // Build LCL product from search params for result display
        if (products.includes('LCL') && (volumeWeightVolume || volumeWeightWeight)) {
            setLastSearchLCLProduct({
                id: 'search-lcl', type: 'LCL', kgOrLbs: 'kg', cmOrInches: 'cm',
                calculations: [{ id: 'search', method: 'volume', volume: parseFloat(volumeWeightVolume) || 0, weight: parseFloat(volumeWeightWeight) || 0, quantity: parseInt(volumeWeightQuantity) || 1 }],
            });
        } else {
            setLastSearchLCLProduct(null);
        }
        // Build Air freight product from search params for result display
        if ((products.includes('Air Freight') || products.includes('Air freight'))) {
            const calculations = [];
            if (dow && dow.length && dow.width && dow.height && dow.weight && dow.quantity) {
                calculations.push({ id: 'search-dim', method: 'dimensions', length: parseFloat(dow.length), width: parseFloat(dow.width), height: parseFloat(dow.height), weight: parseFloat(dow.weight), quantity: parseInt(dow.quantity), commodity: 'General' });
            } else if (volumeWeightVolume && volumeWeightWeight && volumeWeightQuantity) {
                calculations.push({ id: 'search-vol', method: 'volume', volume: parseFloat(volumeWeightVolume), weight: parseFloat(volumeWeightWeight), quantity: parseInt(volumeWeightQuantity), commodity: 'General' });
            } else if (chargeableWeight && chargeableWeightQuantity && chargeableWeightGrossWeight) {
                calculations.push({ id: 'search-cw', method: 'chargeable-weight', chargeableWeight: parseFloat(chargeableWeight), weight: parseFloat(chargeableWeightGrossWeight), quantity: parseInt(chargeableWeightQuantity), commodity: 'General' });
            }
            setLastSearchAirFreightProduct(calculations.length > 0 ? {
                id: 'search-air', type: 'Air freight', kgOrLbs: 'kg', cmOrInches: 'cm',
                dangerous: dangerous || false,
                calculations,
            } : null);
        } else {
            setLastSearchAirFreightProduct(null);
        }
        const index = prevSearch ? prevSearch.created : new Date().getTime();
        if (prevSearch) {
            setSearches(searches => ({ ...searches, [index]: { ...prevSearch, created: index } }));
        } else {
            setSearches(searches => ({
                ...searches,
                [index]: { 
                    created: index, 
                    sources, 
                    destinations, 
                    transportationMethod, 
                    mode: mode,
                    portals: products.includes('LCL') ? false : portals,
                    products, 
                    dangerous,
                    chargeableWeight,
                    chargeableWeightQuantity,
                    chargeableWeightGrossWeight,
                    dow,
                    volumeWeightVolume,
                    volumeWeightWeight,
                    volumeWeightQuantity,
                    weightOrMeasurement,
                    results: [],
                }
            }));
        }

        let results = prevSearch ? prevSearch.results : [];
        let platforms = prevSearch ? prevSearch.platforms : [];

        if (!prevSearch) {

            if (mode.includes('rates')) {
                platforms.push({ mode: 'rates', id: -1, name: 'Monada rates' });
            }

            if (mode.includes('rates') && portals && !products.includes('LCL')) {
                // Check if CargoFive is enabled for rates management
                const cargofiveEnabled = _.get(org, 'integrations.cargofive.enabled', false) && 
                                        _.get(org, 'integrations.cargofive.useForRates', false);
                
                if (transportationMethod === 'sea') {
                    if (cargofiveEnabled) {
                        // Use CargoFive platform instead of OKARGO
                        platforms = [...platforms, {mode: 'rates', id: 101, name: 'Cargofive'}];
                    } else {
                        // Use OKARGO platforms
                        const seaPlatforms = _.get(org, 'integrations.okargo.platforms', []).map(p => ({mode: 'rates', id: p.id, name: (OKARGO_PLATFORMS[p.id] || {}).name }));
                        platforms = [...platforms, ...seaPlatforms];
                    }
                } else {
                    // For non-sea transportation, use portals
                    platforms = [...platforms, {mode: 'rates', id: 100, name: 'portals'}];
                }
                platforms = _.filter(platforms, p => !!p.id);
            }

            if (transportationMethod === 'sea') {
                platforms = _.sortBy(platforms, p => ({
                    'p-1': 1, p3: 2, p8: 3, p2 : 4, p7 : 4, p17 : 4, p21: 4, p101: 5, p5: 7, p19: 8, p13:8, p9: 10,
                    p15: 10, p1: 23, p16: 23, p11: 25, p18: 25, p12: 30}[`p${p.id}`] || 40));
            }

            setSearches(searches => ({
                ...searches,
                [index]: { created: index, sources, destinations, transportationMethod, mode, portals, products, dangerous, platforms, results }
            }));

            if (mode.includes('tariff')) {
                platforms.push({ mode: 'tariff', id: -2, name: 'Monada company tariff' });
            }
        }

        const todoPlatforms = platforms.filter(c => !prevSearch?.donePlatforms.find(d => d.id === c.id))

        for (let modeIndex = 0 ; modeIndex < mode.length ; modeIndex++) {
            const _mode = mode[modeIndex];
            for (let k = 0 ; k < todoPlatforms.filter(p => p.mode === _mode).length ; k++) {
                const _platform = todoPlatforms.filter(p => p.mode === _mode)[k];
                for (let i = 0 ; i < sources.length ; i++) {
                    const source = sources[i];
                    for (let j = 0 ; j < destinations.length ; j++) {
                        const destination = destinations[j];
                        try {
                            const { data } = await _axios.post(`/api/${_mode}/import/online?accesstoken=${accesstoken}&organizationId=${org.id}&platform=${_platform.id}`, {
                                transportationMethod,
                                sourcePort: source,
                                destinationPort: destination,
                                products: products.map(p => {
                                    const baseProduct = createRateProduct({ productType: p, dangerous, transportationMethod, extraFilter: p === 'LCL' ? (p2) => {
                                        if (weightOrMeasurement.postfix === 'cbm') {
                                            return !!p2.volume;
                                        } else if (weightOrMeasurement.postfix === 'ton') {
                                            return !!p2.weight;
                                        }
                                        return false;
                                    } : null });
                                    
                                    // CargoFive: Set default weight to 15,000 kg for sea containers
                                    const isCargoFiveForSearch = transportationMethod === 'sea' && 
                                                                 _.get(org, 'integrations.cargofive.enabled', false) && 
                                                                 _.get(org, 'integrations.cargofive.useForRates', false) &&
                                                                 mode.includes('rates') && 
                                                                 portals &&
                                                                 _platform.id === 101;
                                    
                                    const isSeaContainer = transportationMethod === 'sea' &&
                                                           p !== 'Air Freight' && p !== 'Air freight' &&
                                                           p !== 'LCL';
                                    
                                    return {
                                        ...baseProduct,
                                        // Set default weight to 15,000 kg for CargoFive sea containers if weight is not set
                                        ...(isCargoFiveForSearch && isSeaContainer && !baseProduct.weight ? { weight: 15000, kgOrLbs: 'kg' } : {}),
                                        ...((p === 'Air Freight' || p === 'Air freight') && dow && dow.length && dow.width && dow.height && dow.weight && dow.quantity ? { length: parseFloat(dow.length), width: parseFloat(dow.width), height: parseFloat(dow.height), weight: parseFloat(dow.weight), quantity: parseInt(dow.quantity), cmOrInches: 'cm', kgOrLbs: 'kg' } : {}),
                                        ...((p === 'Air Freight' || p === 'Air freight') && volumeWeightVolume && volumeWeightWeight && volumeWeightQuantity ? { volume: parseFloat(volumeWeightVolume), weight: parseFloat(volumeWeightWeight), quantity: parseInt(volumeWeightQuantity), kgOrLbs: 'kg' } : {}),
                                        ...((p === 'Air Freight' || p === 'Air freight') && chargeableWeight && chargeableWeightQuantity && chargeableWeightGrossWeight ? { chargeableWeight: parseFloat(chargeableWeight), weight: parseFloat(chargeableWeightGrossWeight), quantity: parseInt(chargeableWeightQuantity), kgOrLbs: 'kg' } : {}),
                                    };
                                })
                            });
                            const migratedData = migrateWMRatePairs(data.map(d => migrateRateToAirFreight(migrateRateToLCL({ ...d, type: _mode === 'tariff' ? 'tariff' : d.type, platform: _platform.id }))));
                            results.push(migratedData);
                        } catch (e) {
                            if ((e.response?.data?.detail || e.message) !== 'No offers for specified criteria' &&
                                !((e.response?.data?.detail || e.message).endsWith('has no available offers for your selected criteria at the moment.'))) {
                                results.push({ platform: _platform.id, error: e.response?.data || { detail: e.message } });
                            }
                        }

                        const _results = _.flatten(results).filter(r => {
                            if (!r.product) return true;
                            if ((r.product.dangerous || false) !== (dangerous || false)) return false;
                            if (r.transportationMethod == 'air') return true;
                            const normalizedType = r.product.type === 'By Volume or Weight (sea)' || r.product.type === 'By Dimensions or Weight (sea)' ? 'LCL' : r.product.type;
                            const p = products.filter(pp => pp === normalizedType);
                            if (_.isEmpty(p)) return false;
                            return true;
                        });

                        setSearches(searches => ({
                            ...searches, 
                            [index]: { 
                                ...searches[index], 
                                results: _results, 
                                donePlatforms: _.uniqBy([ ...(searches[index].donePlatforms || []), _platform ], a => a.id)
                            }
                        }));
                    }
                }
            }
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: '0px 12px', width: fullscreen ? '100%' : '530px', height: '100%', overflow: 'hidden' }}>
            <Tabs
                value={page}
                onChange={(event, value) => {
                    setPage(value);
                }}
                sx={{ width: '100%' }}
            >
                <Tab value={0} label='Search' />
                <Tab value={1} label='History' />
            </Tabs>
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                {page === 0 && (
                    <Box sx={{ paddingBottom: '6px', marginTop: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {!showSearchResult && (
                                <Button disabled={!opportunityId && !emailId && !loadingFromOpportunity} color='primary' size='small' onClick={async (e) => {
                                    e.stopPropagation();
                                    setLoadFromOpportunity(opportunityId || emailId);
                                    await new Promise(resolve => setTimeout(resolve, 1000));
                                    setLoadFromOpportunity(null);
                                }} sx={{ marginLeft: '6px', textTransform: 'none', fontWeight: 400 }}>
                                    <FontAwesomeIcon icon={faArrowRightFromLine} />
                                    &nbsp;
                                    Load from Opportunity
                                    {loadingFromOpportunity ? <CircularProgress size={16} sx={{ marginLeft: '6px' }} /> : null}
                                </Button>
                            )}
                            {!showSearchResult && (
                                <Box sx={{ margin: '6px 0px 0px 3px', opacity: 0.1 }}>
                                    |
                                </Box>
                            )}
                            <Button color='primary' size='small' onClick={async (e) => {
                                if (showSearchResult) {
                                    setShowSearchResult(false);
                                    return;
                                }
                                e.stopPropagation();
                                setToggleReset(true);
                                await new Promise(resolve => setTimeout(resolve, 1000));
                                setToggleReset(false);
                            }} sx={{ marginLeft: '3px', textTransform: 'none', fontWeight: 400 }}>
                                <FontAwesomeIcon icon={showSearchResult ? faArrowLeft : faCircleXmark} style={{ marginRight: '6px' }} />
                                {showSearchResult ? 'Edit Search' : 'Reset Search'}
                            </Button>
                        </Box>
                        <Box sx={{ display: showSearchResult ? 'none' : 'block' }}>
                            <SearchWizard
                                setLoadingFromOpportunity={setLoadingFromOpportunity}
                                setErrorLoadingPorts={setErrorLoadingPorts}
                                errorLoadingPorts={errorLoadingPorts}
                                seaPorts={seaPorts}
                                unlocode={unlocode}
                                onSearch={onSearch}
                                setShowSearchResult={setShowSearchResult}
                                opportunities={opportunities}
                                loadFromOpportunity={loadFromOpportunity}
                                toggleReset={toggleReset}
                                fullscreen={fullscreen}
                                organization={org}
                                accesstoken={accesstoken}
                            />
                        </Box>
                    </Box>
                )}
                {page === 1 && activeSearch === -1 && (
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.border1}`, padding: '12px', marginTop: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <Box sx={{ flex: 1 }} />
                            <Button color='error' size='small' sx={{ fontSize: '11px !important' }} onClick={onClearHistory}>
                                <FontAwesomeIcon icon={faCircleXmark} />
                                &nbsp;
                                Clear History
                            </Button>
                        </Box>
                        <SearchHistory searches={searches} activeSearch={activeSearch} setActiveSearch={(v) => { setActiveRates([]); setActiveSearch(v); }} />
                    </Box>
                )}
                {page === 1 && activeSearch > -1 && (
                    <Box sx={{ padding: '12px', marginTop: '12px' }}>
                        <Button
                            color='primary'
                            size='small'
                            onClick={() => { setActiveRates([]); setActiveSearch(-1); }}
                            sx={{ textTransform: 'none', fontWeight: 400, margin: '0px', padding: '0px', minWidth: '0px' }}
                            startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
                        >
                            Back to History
                        </Button>
                    </Box>
                )}
                {(page === 0 || page === 1 || page === 2) && (
                    <Box sx={{ marginTop: '0px', minHeight: 0 }}>
                        {(showSearchResult || (page === 1 && activeSearch > -1)) && (
                            <>
                                <SearchDescription
                                    search={page === 0 ? _.maxBy(_.values(searches), s => s.created) : page === 1 ? searches[activeSearch] : null}
                                    showFilters={true}
                                    filters={filters}
                                    modeFilter={modeFilter}
                                    results={
                                        ((page === 0 ? (showSearchResult ? _.maxBy(_.values(searches), s => s.created) : null) : (activeSearch > -1 ? searches[activeSearch] : null)) || {}).results}
                                />
                                {page === 0 && (
                                    <PlatformFetchingStatus
                                        search={showSearchResult ? _.maxBy(_.values(searches), s => s.created) : null}
                                    />
                                )}
                            </>
                        )}
                        {!showSearchResult && (page === 0 ? _.maxBy(_.values(searches), s => s.created) : page === 1 ? (activeSearch > -1 ? searches[activeSearch] : null) : null) && (
                            <Box sx={{ marginBottom: '12px' }}>
                                <Button
                                    color='primary'
                                    size='small'
                                    onClick={() => setShowSearchResult(true)}
                                    sx={{ textTransform: 'none', fontWeight: 400, margin: '0px', padding: '0px', minWidth: '0px' }}
                                >
                                    Last Search Result
                                </Button>
                            </Box>
                        )}
                        <FilterAndSort
                            search={page === 0 ? _.maxBy(_.values(searches), s => s.created) : page === 1 ? searches[activeSearch] : null}
                            showFilters={true}
                            filters={filters}
                            setFilters={setFilters}
                            sorting={sorting}
                            setSorting={setSorting}
                            modeFilter={modeFilter}
                            setModeFilter={setModeFilter}
                            hideSections={hideSections}
                            setHideSections={setHideSections}
                            results={
                                ((page === 0
                                    ? (showSearchResult ? _.maxBy(_.values(searches), s => s.created) : null)
                                    : (activeSearch > -1 ? searches[activeSearch] : null)
                                ) || {}).results}
                        />
                        <SearchResult
                            search={page === 0 ? (showSearchResult ? _.maxBy(_.values(searches), s => s.created) : null) : page === 1 ? (activeSearch > -1 ? searches[activeSearch] : null) : null}
                            organization={org}
                            activeRates={activeRates}
                            setActiveRate={(v) => setActiveRates(ar => _.xor(ar, [v]))}
                            exchangeRates={exchangeRates}
                            sortingMethod={sorting}
                            filters={filters}
                            modeFilter={modeFilter}
                            setMinimizedSearch={setMinimizedSearch}
                            hideSections={hideSections}
                            onRetry={onRetry}
                            fullscreen={fullscreen}
                            searchLCLProduct={lastSearchLCLProduct}
                            searchAirFreightProduct={lastSearchAirFreightProduct}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    )
}

function SearchWizard(props) {
    const { seaPorts, unlocode, onSearch, setShowSearchResult, opportunities, loadFromOpportunity, toggleReset, 
        fullscreen, organization, accesstoken, setLoadingFromOpportunity, setErrorLoadingPorts, errorLoadingPorts } = props;

    const [ transportationMethod, setTransportationMethod ] = useState('sea');
    const [ sources, setSources ] = useState([]);
    const [ errors, setErrors ] = useState([]);
    const [ destinations, setDestinations ] = useState([]);
    const [ mode, setMode ] = useState(['rates']);
    const [ portals, setPortals ] = useState(true);
    const [ products, setProducts ] = useState([]);
    const [ doorRouteNotice, setDoorRouteNotice ] = useState(null);
    const [ dangerous, setDangerous ] = useState(false);

    // Check if CargoFive is enabled
    const isCargoFive = transportationMethod === 'sea' && 
                       _.get(organization, 'integrations.cargofive.enabled', false) && 
                       _.get(organization, 'integrations.cargofive.useForRates', false) &&
                       mode.includes('rates') && 
                       portals;

    // Air freight checkboxes
    const [ useChargeableWeight, setUseChargeableWeight ] = useState(false);
    const [ useVolumeWeight, setUseVolumeWeight ] = useState(false); 
    const [ useDimensions, setUseDimensions ] = useState(false);

    const [ chargeableWeight, setChargeableWeight ] = useState(0);
    const [ chargeableWeightQuantity, setChargeableWeightQuantity ] = useState(0);
    const [ chargeableWeightGrossWeight, setChargeableWeightGrossWeight ] = useState(0);
    const [ volumeWeightVolume, setVolumeWeightVolume ] = useState('');
    const [ volumeWeightWeight, setVolumeWeightWeight ] = useState('');
    const [ volumeWeightQuantity, setVolumeWeightQuantity ] = useState(0);
    const [ dow, setDow ] = useState({ length: '' , width: '', height: '', weight: '', quantity: 0 });

    useEffect(() => {
        if (loadFromOpportunity) {
            onLoadOpportunity();
        }
    }, [loadFromOpportunity]);

    useEffect(() => {
        if (toggleReset) {
            onReset();
        }
    }, [toggleReset]);

    function onDeleteSource({ s }) {
        setSources(_.without(sources, s));
    }

    function onDeleteDestination({ s }) {
        setDestinations(_.without(destinations, s));
    }

    // CargoFive limits: up to 3 origins OR up to 3 destinations (not both)
    // Rules:
    // - Up to 3 origins into 1 destination: if sources.length >= 2, can only add destination if destinations.length === 0
    // - 1 origin into up to 3 destinations: if sources.length === 1, can add up to 3 destinations
    // - Same logic reversed for sources
    const canAddSource = !isCargoFive || (
        sources.length < 3 && 
        (destinations.length === 0 || destinations.length === 1)
    );
    const canAddDestination = !isCargoFive || (
        destinations.length < 3 && 
        (sources.length === 0 || sources.length === 1 || (sources.length >= 2 && destinations.length === 0))
    );
    const canAddProduct = !isCargoFive || products.length < 3;

    const handleAddSource = (v) => {
        if (canAddSource) {
            setSources([...sources, v]);
        }
        setErrorLoadingPorts(null);
    };

    const handleAddDestination = (v) => {
        if (canAddDestination) {
            setDestinations([...destinations, v]);
        }
        setErrorLoadingPorts(null);
    };

    const handleProductsChange = (value) => {
        const newProducts = typeof value === 'string' ? value.split(',') : value;
        if (!isCargoFive || newProducts.length <= 3) {
            setProducts(newProducts);
        }
    };

    function _onSearch() {
        let weightOrMeasurement = null;
        let e = [];

        if (products.includes('LCL')) {
            const weight = parseFloat(volumeWeightWeight) || 0;
            const volume = parseFloat(volumeWeightVolume) || 0;
            const p = {
                type: 'LCL',
                quantity: 1,
                cmOrInches: 'cm',
                kgOrLbs: 'kg',
                calculations: [{
                    id: 'search',
                    method: 'volume',
                    volume,
                    weight,
                    quantity: 1,
                }],
            };
            weightOrMeasurement = calcWeightOrMeasurment({
                product: p,
                transportationMethod: 'sea',
                allProducts: [p]
            });
        }

        const _products = _.uniq(products);

        if (_.isEmpty(sources) || _.isEmpty(destinations) || _.isEmpty(products)) {
            e = [...e, ..._.compact([
                _.isEmpty(sources) ? 'address-source' : null,
                _.isEmpty(destinations) ? 'address-destination' : null,
                _.isEmpty(products) ? 'products' : null,
            ])];
        }

        // CargoFive validation
        if (isCargoFive) {
            // Validate origin/destination limits: up to 3 origins OR up to 3 destinations (not both)
            if (sources.length > 3) {
                e = [...e, 'cargofive-too-many-sources'];
            }
            if (destinations.length > 3) {
                e = [...e, 'cargofive-too-many-destinations'];
            }
            if (sources.length > 1 && destinations.length > 1) {
                e = [...e, 'cargofive-multiple-origins-destinations'];
            }
            // Validate product limit: up to 3 containers
            if (_products.length > 3) {
                e = [...e, 'cargofive-too-many-products'];
            }
        }

        if ((products.includes('Air Freight') || products.includes('Air freight')) && useChargeableWeight) {
            if ([chargeableWeight, chargeableWeightQuantity, chargeableWeightGrossWeight].filter(d => ((!_.isNaN(parseFloat(d)) && (parseFloat(d) > 0)))).length !== 3) {
                e = [...e, 'air-freight-chargeable-weight'];
            } else if (parseFloat(chargeableWeight) < parseFloat(chargeableWeightGrossWeight)) {
                e = [...e, 'air-freight-chargeable-weight-gross-weight'];
            }
        }

        if ((products.includes('Air Freight') || products.includes('Air freight')) && useDimensions && [dow.quantity,dow.length, dow.width, dow.height, dow.weight].filter(d => ((!_.isNaN(parseFloat(d)) && (parseFloat(d) > 0)))).length !== 5) {
            e = [...e, 'air-freight-dimensions'];
        }

        if ((products.includes('Air Freight') || products.includes('Air freight')) && useVolumeWeight && [volumeWeightQuantity, volumeWeightVolume, volumeWeightWeight].filter(d => ((!_.isNaN(parseFloat(d)) && (parseFloat(d) > 0)))).length !== 3) {
            e = [...e, 'air-freight-volume-weight'];
        }

        if ((products.includes('Air Freight') || products.includes('Air freight')) && !useDimensions && !useVolumeWeight && !useChargeableWeight) {
            e = [...e, 'air-freight-no-fields'];
        }

        console.log(e);

        if (!_.isEmpty(e)) {
            setErrors(e);
            return;
        }
        setErrors([]);
        onSearch({
            sources,
            destinations,
            transportationMethod,
            products: _products, 
            dangerous, 
            mode, 
            portals, 
            weightOrMeasurement, 
            dow: useDimensions ? dow : null, 
            volumeWeightVolume: (useVolumeWeight || _products.includes('LCL')) ? volumeWeightVolume : null,
            volumeWeightWeight: (useVolumeWeight || _products.includes('LCL')) ? volumeWeightWeight : null,
            chargeableWeight: useChargeableWeight ? chargeableWeight : null, 
            chargeableWeightQuantity: useChargeableWeight ? chargeableWeightQuantity : null, 
            chargeableWeightGrossWeight: useChargeableWeight ? chargeableWeightGrossWeight : null, 
            volumeWeightQuantity: (useVolumeWeight || _products.includes('LCL')) ? volumeWeightQuantity : null,
        })
    }

    function onReset() {
        setSources([]);
        setDestinations([]);
        setDoorRouteNotice(null);
        setMode(['rates']);
        setPortals(true);
        setProducts([]);
        setShowSearchResult(false);
        setChargeableWeight(0);
        setChargeableWeightQuantity(0);
        setChargeableWeightGrossWeight(0);
        setVolumeWeightVolume('');
        setVolumeWeightWeight('');
        setVolumeWeightQuantity(0);
        setDow({ length: '' , width: '', height: '', weight: '', quantity: 0 });
        setUseChargeableWeight(false);
        setUseVolumeWeight(false);
        setUseDimensions(false);
        setErrorLoadingPorts(null);
    }

    async function onLoadOpportunity() {
        onReset();
        setLoadingFromOpportunity(true);
        const o = window.searchPaneLoadFromOpportunity?.opportunityId === loadFromOpportunity || (window.searchPaneLoadFromOpportunity?.source?.type === 'email' && window.searchPaneLoadFromOpportunity?.source?.id === loadFromOpportunity) ? 
            window.searchPaneLoadFromOpportunity :
            opportunities.find(o => o.id === loadFromOpportunity);
        if (!o || !o.transportationMethod) return;
        // A route that touches a door (postcode) is a drayage leg — priced under Land, not the
        // opportunity's ocean/air mode. Load it as a Land search so it matches the door rates, and
        // flag it so the rep knows the ocean/air leg is priced as a separate leg.
        const doorRatesOn = _.get(organization, 'integrations.doorRates.enabled', false);
        const doorEndpoints = _.compact((o.routes || []).flatMap(r => [r.source, r.destination])).filter(l => l?.type === 'postcode');
        const useLandForDoor = doorRatesOn && doorEndpoints.length > 0 && o.transportationMethod !== 'land';
        setTransportationMethod(useLandForDoor ? 'land' : o.transportationMethod);
        setDoorRouteNotice(useLandForDoor ? { doors: _.uniq(doorEndpoints.map(l => l.text || l.city || l.id)), mode: o.transportationMethod } : null);
        setProducts(_.uniq(o.products.map(p => {
            if (p.type === 'By Dimensions or Weight (sea)') return 'LCL';
            if (p.type === 'By Volume or Weight (sea)') return 'LCL';
            if (p.type === 'Air freight') return 'Air Freight';
            return p.type;
        })));

        const [_sources, _destinations] = await Promise.all([
            Promise.all(o.routes.map(r => getPort({ unlocode, accesstoken, source: r.source, organization, transportationMethod: o.transportationMethod }))),
            Promise.all(o.routes.map(r => getPort({ unlocode, accesstoken, source: r.destination, organization, transportationMethod: o.transportationMethod })))
        ]);

        if (_.some(_sources, s => !s) || _.some(_destinations, d => !d)) {
            const missing = _.compact([
                ...o.routes.map((r, i) => !_sources[i] ? r.source : null),
                ...o.routes.map((r, i) => !_destinations[i] ? r.destination : null)
            ]);
            setErrorLoadingPorts(missing.length > 0 ? missing : null);
        }

        setSources(_.compact(_sources));
        setDestinations(_.compact(_destinations));

        setDangerous(o.products.find(p => p.dangerous));

        if (o.transportationMethod === 'sea') {
            const lclProducts = o.products.filter(p => p.type === 'LCL');
            let totalCbm = 0;
            let totalKg = 0;
            lclProducts.forEach(p => {
                const vol = calcTotalVolume({ product: p });
                totalCbm += vol.postfix === 'cft' ? (vol.val || 0) * 0.0283168 : (vol.val || 0);
                const weightUnit = p.kgOrLbs || 'kg';
                for (const calc of (p.calculations || [])) {
                    const w = (calc.weight || 0) * (calc.quantity || 1);
                    totalKg += weightUnit === 'lbs' ? w * 0.453592 : w;
                }
            });
            setVolumeWeightQuantity(1);
            setVolumeWeightVolume(totalCbm);
            setVolumeWeightWeight(totalKg);
        } else {
            const convertToCm = (value, unit) => {
                if (!value) return value;
                return unit === 'in.' ? parseFloat(value) * 2.54 : parseFloat(value);
            };
            const convertToKg = (value, unit) => {
                if (!value) return value;
                return unit === 'lbs' ? parseFloat(value) / 2.20462 : parseFloat(value);
            };

            // Gather all calculations across all Air freight products (normalized to cm/kg)
            const airProducts = o.products.filter(p => p.type === 'Air Freight' || p.type === 'Air freight');
            const allCalcs = [];
            airProducts.forEach(p => {
                const lengthUnit = p.cmOrInches || 'cm';
                const weightUnit = p.kgOrLbs || 'kg';
                if ((p.calculations || []).length > 0) {
                    p.calculations.forEach(c => {
                        allCalcs.push({
                            method: c.method,
                            quantity: c.quantity || 1,
                            length: convertToCm(c.length, lengthUnit),
                            width: convertToCm(c.width, lengthUnit),
                            height: convertToCm(c.height, lengthUnit),
                            volume: c.volume,
                            weight: convertToKg(c.weight, weightUnit),
                            chargeableWeight: convertToKg(c.chargeableWeight, weightUnit),
                        });
                    });
                } else {
                    // Legacy: single calculation from top-level fields
                    const method = p.volume ? 'volume' : (p.length && p.width && p.height ? 'dimensions' : 'chargeable-weight');
                    allCalcs.push({
                        method,
                        quantity: p.quantity || 1,
                        length: convertToCm(p.length, lengthUnit),
                        width: convertToCm(p.width, lengthUnit),
                        height: convertToCm(p.height, lengthUnit),
                        volume: p.volume,
                        weight: convertToKg(p.weight, weightUnit),
                        chargeableWeight: convertToKg(p.chargeableWeight, weightUnit),
                    });
                }
            });

            if (allCalcs.length === 1) {
                const c = allCalcs[0];
                if (c.method === 'dimensions') {
                    setDow({ length: c.length, width: c.width, height: c.height, weight: c.weight, quantity: c.quantity });
                    setUseDimensions(true);
                } else if (c.method === 'volume') {
                    setVolumeWeightVolume(c.volume);
                    setVolumeWeightWeight(c.weight);
                    setVolumeWeightQuantity(c.quantity);
                    setUseVolumeWeight(true);
                } else {
                    setChargeableWeight(c.chargeableWeight);
                    setChargeableWeightGrossWeight(c.weight);
                    setChargeableWeightQuantity(c.quantity);
                    setUseChargeableWeight(true);
                }
            } else if (allCalcs.length > 1) {
                // Multiple calculations: summarize as single chargeable-weight product
                const totalCW = _.sumBy(airProducts, p => {
                    const cw = calcChargeableWeight({ product: p, transportationMethod: 'air', allProducts: airProducts, round: organization.airRoundChargeableWeight });
                    if (!cw) return 0;
                    const postfix = cw.postfix || 'kg';
                    return postfix === 'kg' ? cw.val : cw.val / 2.20462;
                });
                const totalGW = _.sumBy(airProducts, p => {
                    const w = calcWeight({ product: p });
                    const postfix = w.postfix || 'kg';
                    return postfix === 'kg' ? w.val : w.val / 2.20462;
                });
                setChargeableWeight(totalCW);
                setChargeableWeightGrossWeight(totalGW);
                setChargeableWeightQuantity(1);
                setUseChargeableWeight(true);
            }
        }
        setLoadingFromOpportunity(false);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', marginTop: '6px', alignItems: 'center' }}>
                <Typography variant='caption' component={Box} sx={{ flex: 1, fontWeight: 800 }}>Route:</Typography>
            </Box>
            <Box sx={{ padding: '3px 0px' }}>
                <TransportationMethod 
                    disabled={!setTransportationMethod}
                    transportationMethod={transportationMethod}
                    setTransportationMethod={(v) => {
                        setProducts([]);
                        setTransportationMethod(v);
                    }}
                    size={'small'}
                    width={'100%'}
                    disabledMethods={_.get(organization, 'integrations.doorRates.enabled', false) ? [] : ['land']}
                />
            </Box>
            <Where 
                transportationMethod={transportationMethod}
                errors={errors}
                setSource={handleAddSource}
                setDestination={handleAddDestination}
                seaPorts={seaPorts}
                unlocode={unlocode}
                nogmaps={transportationMethod !== 'land'}
                organization={organization}
                accesstoken={accesstoken}
                disabledSource={!canAddSource}
                disabledDestination={!canAddDestination}
            />
            <Box sx={{ display: 'flex', gap: '8px' }}>
                <Box sx={{ flex: 1, paddingTop: '3px' }}>
                    {isCargoFive && (
                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            {sources.length}/{destinations.length > 1 ? 1 : 3} {sources.length === 1 ? 'origin' : 'origins'}
                            {destinations.length > 1 && ' (max 1 destination when using multiple origins)'}
                        </Typography>
                    )}
                    {sources.map(s => ( 
                        <Chip 
                            key={s.text} 
                            label={(
                                <span>
                                    <FontAwesomeIcon icon={locationIcon(s)} style={{ marginRight: '6px' }} />
                                    <span>{s.text}</span>
                                </span>
                            )} 
                            sx={{ margin: '3px', maxWidth: '243px' }} 
                            onDelete={() => onDeleteSource({s})} 
                        />
                    ))}
                </Box>
                <Box sx={{ flex: 1, paddingTop: '3px' }}>
                    {isCargoFive && (
                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                            {destinations.length}/{sources.length > 1 ? 1 : 3} {destinations.length === 1 ? 'destination' : 'destinations'}
                            {sources.length > 1 && ' (max 1 origin when using multiple destinations)'}
                        </Typography>
                    )}
                    {destinations.map(s => ( 
                        <Chip 
                            key={s.text} 
                            label={(
                                <span>
                                    <FontAwesomeIcon icon={locationIcon(s)} style={{ marginRight: '6px' }} />
                                    <span>{s.text}</span>
                                </span>
                            )} 
                            sx={{ margin: '3px', maxWidth: '243px' }} 
                            onDelete={() => onDeleteDestination({s})}
                        />
                    ))}
                </Box>
            </Box>
            {errorLoadingPorts && (
                <Box sx={{ marginTop: '12px' }}>
                    <Alert
                        severity='warning'
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => setErrorLoadingPorts(null)}
                            >
                                <FontAwesomeIcon icon={faCircleXmark} fontSize="12px" />
                            </IconButton>
                        }
                    >
                        The following ports could be loaded since they are not supported by CargoFive:
                        <ul>
                            {errorLoadingPorts.map(p => <li key={p.text}>{p.text}</li>)}
                        </ul>
                    </Alert>
                </Box>
            )}
            {doorRouteNotice && (
                <Box sx={{ marginTop: '12px' }}>
                    <Alert severity='info' onClose={() => setDoorRouteNotice(null)}>
                        <b>{doorRouteNotice.doors.join(', ')}</b> {doorRouteNotice.doors.length > 1 ? 'are door locations' : 'is a door location'} — a door leg is priced as <b>Land</b> drayage (port&nbsp;↔&nbsp;door), so this was loaded as a <b>Land</b> search (it will match your door rates). The {doorRouteNotice.mode === 'air' ? 'air' : 'ocean'} leg is priced separately as a port-to-port search.
                    </Alert>
                </Box>
            )}

            <Box sx={{ display: 'flex', marginTop: '6px', alignItems: 'center' }}>
                <Typography variant='caption' component={Box} sx={{ flex: 1, fontWeight: 800 }}>
                    Products:
                    {isCargoFive && (
                        <span style={{ fontWeight: 400, marginLeft: '6px', color: products.length >= 3 ? 'error.main' : 'text.secondary' }}>
                            ({products.length}/3)
                        </span>
                    )}
                </Typography>
                <FormControlLabel
                    control={<Switch size='small' checked={dangerous} onChange={() => setDangerous(!dangerous)} />}
                    label=<Typography variant='caption'>Dangerous</Typography>
                />
            </Box>
            <Box sx={{ display: 'flex', marginTop: '3px', alignItems: 'flex-start' }}>
                <FormControl sx={{ margin: '0px 6px 0px 0px' }} fullWidth size='small'>
                    <Select
                        multiple
                        error={errors.includes('products')}
                        value={products}
                        disabled={isCargoFive && products.length >= 3 && !canAddProduct}
                        onChange={(e) => {
                            handleProductsChange(e.target.value);
                        }}
                        input={<OutlinedInput placeholder='Products' />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} onMouseDown={e => { if (typeof e.target.className !== 'string' || e.target.className.indexOf('MuiChip-label') === -1) { e.stopPropagation(); e.preventDefault();}}} onDelete={e => { 
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setProducts(p => _.without(p, value));
                                    }}/>
                                ))}
                            </Box>
                        )}
                        MenuProps={{
                            sx: {
                                zIndex: 100000000,
                            },
                            PaperProps: {
                                style: {
                                    maxHeight: 48 * 4.5 + 8,
                                    width: 250,
                                },
                            }
                        }}
                    >
                        {transportationMethod === 'land' ? (
                            /* Land: grouped to match the opportunity product picker (Containers / LCL / Air / Trucks). */
                            ['Containers', 'LCL', 'Air', 'Trucks'].flatMap(group => {
                                const items = (PRODUCT_TYPES.land || []).filter(t => t.group === group);
                                if (!items.length) return [];
                                return [
                                    <ListSubheader key={`group-${group}`}>{group}</ListSubheader>,
                                    ...items.map(product => {
                                        const isSelected = products.indexOf(product.name) > -1;
                                        return (
                                            <MenuItem key={product.name} value={product.name}>
                                                <Checkbox checked={isSelected} />
                                                <ListItemText primary={product.name} />
                                            </MenuItem>
                                        );
                                    }),
                                ];
                            })
                        ) : (
                            PRODUCT_TYPES[transportationMethod].filter(p => p.name !== 'By Dimensions or Weight (sea)' && p.name !== 'By Volume or Weight (sea)' && p.name !== 'LCL' && p.name !== 'Air Freight').map((product) => {
                                const isSelected = products.indexOf(product.name) > -1;
                                const isDisabled = isCargoFive && !isSelected && products.length >= 3;
                                return (
                                    <MenuItem
                                        key={product.name}
                                        value={product.name}
                                        disabled={isDisabled}
                                        sx={isDisabled ? { opacity: 0.5 } : {}}
                                    >
                                        <Checkbox checked={isSelected} disabled={isDisabled} />
                                        <ListItemText primary={product.name} />
                                    </MenuItem>
                                );
                            })
                        )}
                        {transportationMethod === 'sea' && (() => {
                            const isSelected = products.indexOf('LCL') > -1;
                            const isDisabled = isCargoFive && !isSelected && products.length >= 3;
                            return (
                                <MenuItem key='LCL' value='LCL' disabled={isDisabled} sx={isDisabled ? { opacity: 0.5 } : {}}>
                                    <Checkbox checked={isSelected} disabled={isDisabled} />
                                    <ListItemText primary='LCL' />
                                </MenuItem>
                            );
                        })()}
                    </Select>
                </FormControl>
            </Box>

            {(products.includes('Air Freight') || products.includes('Air freight')) && errors.includes('air-freight-no-fields') && (
                <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>Please pick at least one type of freight</Typography>
            )}
            
            {/* CargoFive error messages */}
            {isCargoFive && errors.includes('cargofive-too-many-sources') && (
                <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>CargoFive supports up to 3 origins. Please remove {sources.length - 3} origin{sources.length - 3 > 1 ? 's' : ''}.</Typography>
            )}
            {isCargoFive && errors.includes('cargofive-too-many-destinations') && (
                <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>CargoFive supports up to 3 destinations. Please remove {destinations.length - 3} destination{destinations.length - 3 > 1 ? 's' : ''}.</Typography>
            )}
            {isCargoFive && errors.includes('cargofive-multiple-origins-destinations') && (
                <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>CargoFive supports up to 3 origins OR up to 3 destinations, but not both. Please use either multiple origins with 1 destination, or 1 origin with multiple destinations.</Typography>
            )}
            {isCargoFive && errors.includes('cargofive-too-many-products') && (
                <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>CargoFive supports up to 3 container types. Please remove {products.length - 3} container{products.length - 3 > 1 ? 's' : ''}.</Typography>
            )}
            
            {/* CargoFive info message */}
            {isCargoFive && products.length > 0 && (
                <Typography variant='caption' component={Box} sx={{ flex: 1, marginTop: '6px', color: 'text.secondary', fontSize: '10px' }}>
                    Note: CargoFive default weight is 15,000 kg per container.
                </Typography>
            )}

            {(products.includes('Air Freight') || products.includes('Air freight')) && (
                <>
                    <Box sx={{ display: 'flex', marginTop: '12px', alignItems: 'center' }}>
                        <FormControlLabel
                            control={<Checkbox checked={useChargeableWeight} onChange={() => setUseChargeableWeight(!useChargeableWeight)} size='small'/>}
                            label='Use chargeable weight'
                        />
                    </Box>
                    <Box sx={{ display: 'flex', marginTop: '12px' }}>
                        <TextField
                            sx={{ flex: 0, minWidth: '80px', maxWidth: '80px' }}
                            value={chargeableWeightQuantity}
                            onChange={e => setChargeableWeightQuantity(e.target.value)}
                            label={'Pieces'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-chargeable-weight')}
                            disabled={!useChargeableWeight}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={chargeableWeight}
                            onChange={e => setChargeableWeight(e.target.value)}
                            label={'Chargeable Weight (kg) per piece'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-chargeable-weight') || errors.includes('air-freight-chargeable-weight-gross-weight')}
                            disabled={!useChargeableWeight}
                        />
                        <TextField
                            sx={{ flex: 0, minWidth: '200px' }}
                            value={chargeableWeightGrossWeight}
                            onChange={e => setChargeableWeightGrossWeight(e.target.value)}
                            label={'Gross Weight (kg) per piece'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-chargeable-weight') || errors.includes('air-freight-chargeable-weight-gross-weight')}
                            disabled={!useChargeableWeight}
                        />
                    </Box>
                    {errors.includes('air-freight-chargeable-weight') && <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>Please fill all fields with a positive number</Typography>}
                    {errors.includes('air-freight-chargeable-weight-gross-weight') && <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>Chargeable weight must be equal or greater than gross weight</Typography>}
                </>
            )}

            { (products.includes('LCL') || products.includes('By Volume or Weight (sea)') || (products.includes('Air Freight') || products.includes('Air freight'))) && (
                <>
                    {(products.includes('Air Freight') || products.includes('Air freight')) && (
                        <Box sx={{ display: 'flex', marginTop: '12px', alignItems: 'center' }}>
                            <FormControlLabel
                                control={<Checkbox checked={useVolumeWeight} onChange={() => setUseVolumeWeight(!useVolumeWeight)} size='small'/>}
                                label='Use volume weight'
                            />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', marginTop: '12px' }}>
                        <TextField
                            sx={{ flex: 0, minWidth: '80px', maxWidth: '80px' }}
                            value={volumeWeightQuantity}
                            onChange={e => setVolumeWeightQuantity(e.target.value)}
                            label={(products.includes('Air Freight') || products.includes('Air freight')) ? 'Pieces' : 'Quantity'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-volume-weight')}
                            disabled={(products.includes('Air Freight') || products.includes('Air freight')) ? !useVolumeWeight : false}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={volumeWeightVolume}
                            onChange={e => setVolumeWeightVolume(e.target.value)}
                            label={(products.includes('Air Freight') || products.includes('Air freight')) ? 'Volume (cbm) per piece' : 'Volume (cbm)'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-volume-weight')}
                            disabled={(products.includes('Air Freight') || products.includes('Air freight')) ? !useVolumeWeight : false}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={volumeWeightWeight}
                            onChange={e => setVolumeWeightWeight(e.target.value)}
                            label={(products.includes('Air Freight') || products.includes('Air freight')) ? 'Gross Weight (kg) per piece' : 'Weight (kg)'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            error={errors.includes('air-freight-volume-weight')}
                            disabled={(products.includes('Air Freight') || products.includes('Air freight')) ? !useVolumeWeight : false}
                        />
                    </Box>
                    {errors.includes('air-freight-volume-weight') && <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>Please fill all fields with a positive number</Typography>}
                </>
            )}

            {(products.includes('Air Freight') || products.includes('Air freight')) && (
                <>
                    <Box sx={{ display: 'flex', marginTop: '12px', alignItems: 'center' }}>
                        <FormControlLabel
                            control={<Checkbox checked={useDimensions} onChange={() => setUseDimensions(!useDimensions)} size='small'/>}
                            label='Use dimensions'
                        />
                    </Box>
                    <Box sx={{ display: 'flex', marginTop: '12px' }}>
                        <TextField
                            sx={{ flex: 0, width: '80px', maxWidth: '80px', minWidth: '80px' }}
                            value={dow.quantity}
                            onChange={e => setDow({ ...dow, quantity: e.target.value })}
                            error={errors.includes('air-freight-dimensions')}
                            label={'Pieces'}
                            placeholder="0"
                            InputLabelProps={{ shrink: true }}
                            disabled={!useDimensions}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={dow.length}
                            onChange={e => setDow({ ...dow, length: e.target.value })}
                            label={'Length (cm) per piece'}
                            placeholder="0"
                            error={errors.includes('air-freight-dimensions')}
                            InputLabelProps={{ shrink: true }}
                            disabled={!useDimensions}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={dow.width}
                            onChange={e => setDow({ ...dow, width: e.target.value })}
                            label={'Width (cm) per piece'}
                            placeholder="0"
                            error={errors.includes('air-freight-dimensions')}
                            InputLabelProps={{ shrink: true }}
                            disabled={!useDimensions}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={dow.height}
                            onChange={e => setDow({ ...dow, height: e.target.value })}
                            label={'Height (cm) per piece'}
                            placeholder="0"
                            error={errors.includes('air-freight-dimensions')}
                            InputLabelProps={{ shrink: true }}
                            disabled={!useDimensions}
                        />
                        <TextField
                            sx={{ flex: 1 }}
                            value={dow.weight}
                            onChange={e => setDow({ ...dow, weight: e.target.value })}
                            label={'Weight (kg) per piece'}
                            placeholder="0"
                            error={errors.includes('air-freight-dimensions')}
                            InputLabelProps={{ shrink: true }}
                            disabled={!useDimensions}
                        />
                    </Box>
                    {errors.includes('air-freight-dimensions') && <Typography variant='caption' component={Box} color='error' sx={{ flex: 1, marginTop: '12px' }}>Please fill all fields with a positive number</Typography>}
                </>
            )}

            <Box sx={{ display: 'flex', marginTop: '12px', alignItems: 'center' }}>
                <Typography variant='caption' component={Box} sx={{ flex: 1, fontWeight: 800 }}>Search for:</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
                <FormControl sx={{ display: 'flex', flexDirection: 'row' }}>
                    <FormControlLabel control={<Checkbox checked={mode.includes('rates')} onChange={() => setMode(m => _.xor(m, ['rates']))} size='small'/>} label={<Typography component={Box} variant='caption'>Supplier Rates (</Typography> } />
                    <FormControlLabel control={<Switch size='small' disabled={!mode.includes('rates')} checked={portals} onChange={e => setPortals(!portals)} />} label={<Typography variant='caption'>Including portals&nbsp;&nbsp;)</Typography>} />
                </FormControl>
            </Box>
            <Button disabled={mode.length === 0} fullWidth variant='contained' color='primary' onClick={_onSearch}>
                Search
            </Button>
        </Box>
    )
}

function SearchHistory(props) {
    const { searches, activeSearch, setActiveSearch } = props; 
    const theme = useTheme();

    if (_.keys(searches).length === 0) {
        return null;
    }

    return (
        <Box>
            {_(searches).keys().sortBy(a => parseInt(a)).reverse().map(index => searches[index]).map(search =>
                <MenuItem
                    key={search.created} 
                    selected={activeSearch === search.created} 
                    onClick={() => setActiveSearch(search.created)} 
                    sx={{ borderBottom: '1px solid #eee', padding: '12px 3px', flexDirection: 'row', justifyContent: 'start', whiteSpace: 'wrap' }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: '12px', fontSize: 14 }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ fontWeight: '800', fontSize: '11px', color: theme.palette.primary.main }}>
                            {title(search)}
                        </Box>
                        <Box sx={{ fontSize: '11px' }}>
                            <div dangerouslySetInnerHTML={{ __html: description(search) }} />
                        </Box>
                    </Box>
                </MenuItem>
            ).value()}
        </Box>
    )
}


// A rate that should carry validity dates but has none - or has something dayjs cannot
// read - is not safe to quote from: the card either prints "Valid from: Invalid Date" or
// a date it invented, and either way there is no telling whether the rate still applies.
// Keep those out of search results entirely.
//
// The check is scoped the same way RateCard decides whether to show a validity line at
// all (RateCard.js:146). A live spot result from a platform never shows one and genuinely
// has no dates, so filtering on validity would remove every live result; tariffs are
// excluded for the same reason. Only rates expected to carry dates are judged on them.
//
// dayjs rather than Date.parse, because they disagree on exactly the values that matter:
// Date.parse waves '' and null through, while dayjs - which is what the card renders
// with - reports both as invalid.
function hasUnusableValidity(rate) {
    const expectsValidity = (rate?.platform === -1 && rate?.type !== 'tariff')
        || rate?.type === 'contract'
        || !!rate?.attributes?.cargofiveRate;
    if (!expectsValidity) return false;

    const usable = value => value !== undefined && value !== null && value !== '' && dayjs(value).isValid();
    return !usable(rate?.offer?.validFrom) || !usable(rate?.offer?.validUntil);
}

function SearchResult(props) {
    const {
        search, organization, activeRates, setActiveRate, exchangeRates,
        sortingMethod, filters, modeFilter, setMinimizedSearch, hideSections, onRetry, fullscreen, searchLCLProduct: searchLCLProductProp, searchAirFreightProduct } = props;

    const [ showErrors, setShowErrors ] = useState(false);
    const theme = useTheme();

    const searchLCLProduct = searchLCLProductProp;

    if (!search) {
        return null;
    }
    const donePlatforms = search.donePlatforms || [];

    const allResults = _(search.results).filter(r => !r.error && donePlatforms.find(p => p.id === r.platform)).reject(hasUnusableValidity).value();
    const allResultsSections = allResults.map(result => ({
        ...result,
        offer: {
            ...result.offer,
            sections: _.compact(result.offer.sections.map(s => hideSections.includes(s.title) ? null : s))
        }
    }));
    const allResultsSpread = _.flatten(allResultsSections.map(r => _.range((r.offer.transitDates || []).length || 1).map(t => ([t, r]))));
    const sortedResults = (() => {
        // Parse sorting method (format: "field" or "field-direction")
        const parts = (sortingMethod || 'price').split('-');
        const field = parts[0];
        const direction = parts[1] || 'asc';

        let sorted;
        if (field === 'price') {
            sorted = _.sortBy(allResultsSpread, r => {
                const rateProducts = (searchLCLProduct && r[1].product?.type === 'LCL')
                    ? [{ ...r[1].product, calculations: searchLCLProduct.calculations, kgOrLbs: searchLCLProduct.kgOrLbs, cmOrInches: searchLCLProduct.cmOrInches }]
                    : (searchAirFreightProduct && r[1].product?.type === 'Air freight')
                    ? [{ ...r[1].product, calculations: searchAirFreightProduct.calculations, kgOrLbs: searchAirFreightProduct.kgOrLbs, cmOrInches: searchAirFreightProduct.cmOrInches }]
                    : [r[1].product];
                const totals = calcOfferSum({ offer: r[1].offer, products: rateProducts, transportationMethod: r[1].transportationMethod, exchangeRates, organization });
                return totals.max.normalized;
            });
        } else if (field === 'departureTime') {
            sorted = _.sortBy(allResultsSpread, r => new Date(((r[1].offer.transitDates || [])[r[0] || 0] || {}).etd || 0).getTime());
        } else if (field === 'arrivalTime') {
            sorted = _.sortBy(allResultsSpread, r => new Date(((r[1].offer.transitDates || [])[r[0] || 0] || {}).eta || 0).getTime());
        } else if (field === 'transitTime') {
            sorted = _.sortBy(allResultsSpread, r => r[1].offer.transitTime === -1 ? Number.MAX_VALUE : r[1].offer.transitTime);
        } else if (field === 'carrier') {
            sorted = _.sortBy(allResultsSpread, r => r[1].supplier.organization);
        } else {
            sorted = allResultsSpread;
        }

        // Reverse if descending
        return direction === 'desc' ? sorted.reverse() : sorted;
    })();

    let index = 1;
    const count = sortedResults.filter(([tdIndex, rate]) => shouldRenderRateCard({ rate, transitDatesIndex: tdIndex }) && ((_.isEmpty(filters)) || (filters.map(f => f.func(rate)).every(f => f))) && ((rate.type === 'tariff' ? 'tariff' : 'rates') === modeFilter)).length;

    const noResults = donePlatforms.filter(platform => {
        const results = _(search.results).filter(r => !r.error && r.platform === platform.id).reject(hasUnusableValidity).sortBy(r => r.supplier?.organization).value();
        const errors = _(search.results).filter(e => !!e.error && e.platform === platform.id).value();
        return errors.length === 0 && results.length === 0;
    });

    const errors = donePlatforms.filter(platform => {
        const errors = _(search.results).filter(e => !!e.error && e.platform === platform.id).value();
        return errors.length > 0;
    });

    return (
        <Box sx={{ whiteSpace: 'nowrap', overflowX: 'auto', width: fullscreen ? '100%' :  '500px', paddingBottom: '30px' }}>
            {sortedResults.map(([tdIndex, rate]) => {
                if (!shouldRenderRateCard({ rate, transitDatesIndex: tdIndex })) return null;
                if (rate.type === 'tariff' && modeFilter !== 'tariff') return null;
                if (rate.type !== 'tariff' && modeFilter === 'tariff') return null;

                const card = (
                    <RateCard
                        organization={organization}
                        rate={rate}
                        onClick={e => { e.stopPropagation(); setActiveRate(`${rate.id}-${tdIndex}`) }}
                        exchangeRates={exchangeRates}
                        transitDatesIndex={tdIndex}
                        countStr={`${index++} / ${count}`}
                        onStartDragging={() => {
                            setMinimizedSearch(true);
                        }}
                        onEndDragging={() => {
                            setMinimizedSearch(false);
                        }}
                        opportunityProducts={
                            (searchLCLProduct && rate.product?.type === 'LCL') ? [searchLCLProduct] :
                            (searchAirFreightProduct && rate.product?.type === 'Air freight') ? [searchAirFreightProduct] :
                            undefined
                        }
                    />
                );

                if ((_.isEmpty(filters)) || (filters.map(f => f.func(rate)).every(f => f))) return (
                    <ThemeProvider theme={accordiontheme}>
                        <Accordion
                            key={`${rate.id}-${tdIndex}`}
                            expanded={activeRates.includes(`${rate.id}-${tdIndex}`)}
                            onChange={() => setActiveRate(`${rate.id}-${tdIndex}`)}
                            elevation={0}
                            sx={{ border: `1px solid ${theme.palette.border1}`, marginBottom: '12px' }}
                        >
                            <AccordionSummary sx={{ margin: '0px', padding: '0px' }}>
                                {card}
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '0px' }}>
                                <InnerRatesDetails
                                    rate={rate}
                                    organization={organization}
                                    setMinimizedSearch={setMinimizedSearch}
                                    searchLCLProduct={searchLCLProduct}
                                    searchAirFreightProduct={searchAirFreightProduct}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </ThemeProvider>
                );
                return null;
            })}

            {noResults.length > 0 && (
                <Box sx={{ paddingBottom: '3px' }}>
                    <Alert severity='warning' sx={{ textWrap: 'wrap', fontSize: '11px' }}>
                        No available rates within the next {search.transportationMethod === 'air' ? 5 : 30} days for: {noResults.map(a => a.name).join(', ')}
                    </Alert>
                </Box>
            )}

            {errors.length > 0 && !showErrors && (
                <Alert 
                    severity='error'
                    sx={{ textWrap: 'wrap', marginBottom: '20px', fontSize: '11px' }}
                    action={
                        <Button color='inherit' size='small' sx={{ textTransform: 'none', fontSize: '11px !important' }} onClick={() => setShowErrors(true)}>
                            Expand
                        </Button>
                    }
                >
                    Some carriers had problems sending results: {errors.map(a => a.name).join(', ')}
                </Alert>
            )}

            {errors.length > 0 && showErrors ? (
                <Box>
                    {donePlatforms.sort((a,b) => {
                        const errorsA = _(search.results).filter(e => !!e.error && e.platform === a.id).value();
                        const errorsB = _(search.results).filter(e => !!e.error && e.platform === b.id).value();
                        if (errorsA.length > 0 && errorsB.length > 0) return 0;
                        if (errorsA.length > 0) return -1;
                        if (errorsB.length > 0) return 1;
                        return 0;
                    }).map(platform => {
                        const results = _(search.results).filter(r => !r.error && r.platform === platform.id).reject(hasUnusableValidity).sortBy(r => r.supplier?.organization).value();
                        const errors = _(search.results).filter(e => !!e.error && e.platform === platform.id).value();

                        if (errors.length > 0) {
                            return (
                                <Alert
                                    severity='error'
                                    sx={{ textWrap: 'wrap', marginBottom: '3px', fontSize: '11px' }}
                                    action={
                                        <IconButton color='inherit' size='small' sx={{ marginTop: '3px' }} onClick={() => onRetry({ platform })}>
                                            <FontAwesomeIcon icon={faRotateRight} />
                                        </IconButton>
                                    }
                                >
                                    {errors.map(e => e.error.detail === 'Invalid tokens.' ? `No token defined for source airport on ${platform.name}.` : e.error.detail).join('')}
                                </Alert>
                            )
                        }

                        return null;
                    })}
                    <Button color='error' size='small' sx={{ textTransform: 'none', fontSize: '11px !important' }} onClick={() => setShowErrors(false)}>
                        Collapse errors
                    </Button>
                </Box>
            ) : null}
        </Box>
    )
}

function title(search) {
    const date = new Date(search.created);
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const time = date.toTimeString().split(' ')[0];
    const dateStr = `${day}-${month}-${year}`;
    
    return (
        <>
            Search from: {dateStr}, {time}
        </>
    );
}

function description(search) {
    let str = `<b>${_.startCase(search.transportationMethod)}</b> transport of <b>${oxfordJoin(search.products)}</b>`;
    str += ` from <b>${oxfordJoin(search.sources.map(s => s.text))}</b>`;
    str += ` to <b>${oxfordJoin(search.destinations.map(d => d.text))}</b>`;
    str += ' using <b>';
    if (search.mode.length === 2) {
        str += 'local rates and company tariff';
    } else if (search.mode === 'rates') {
        str += 'local rates';
    } else {
        str += 'company tariff';
    }
    if (search.portals) {
        str += ' and portals';
    } else {
        str += '';
    }
    return str + '</b>.';
}

function SearchDescription(props) {
    const { search, results, filters, modeFilter, showFilters } = props;
    const theme = useTheme();

    if (!search) return null;

    // Count what is actually rendered: the same rates the result list drops must not be
    // counted here, or the header claims more results than the list shows.
    const shown = results ? results.filter(r => !hasUnusableValidity(r)) : [];
    const filtered = shown.filter(r => !r.error && ((r.type === 'tariff' ? 'tariff' : 'rates') === modeFilter) && filters.every(f => f.func(r)));
    const nonErrorResults = shown.filter(r => !r.error);

    // New format: "Search for Product_type {Origin} to {Destination}."
    const productTypeStr = search.products && search.products.length > 0 
        ? oxfordJoin(search.products)
        : 'products';
    
    const originStr = search.sources && search.sources.length > 0
        ? oxfordJoin(search.sources.map(s => s.text))
        : '';
    
    const destinationStr = search.destinations && search.destinations.length > 0
        ? oxfordJoin(search.destinations.map(d => d.text))
        : '';

    let searchDescription = `Search for <b>${productTypeStr}</b> ${originStr ? `<b>${originStr}</b>` : ''}${destinationStr ? ` to <b>${destinationStr}</b>` : ''}.`;
    
    if (results && results.length > 0) {
        if (filtered.length !== nonErrorResults.length && nonErrorResults.length > 0) {
            searchDescription += ` Found <b>${nonErrorResults?.length} ${pluralize('result', nonErrorResults?.length)}</b> (Filtered to <b>${filtered.length} results</b>)`;
        } else if (nonErrorResults.length > 0) {
            searchDescription += ` Found <b>${nonErrorResults?.length} ${pluralize('result', nonErrorResults?.length)}</b>.`;
        }
    }

    return (
        <Box sx={{ marginBottom: '6px' }}>
            {showFilters && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    <Alert
                        severity='info'
                        sx={{ textWrap: 'wrap', marginBottom: '3px', fontSize: '11px', '& .MuiAlert-icon': { alignItems: 'center', display: 'flex' } }}
                        icon={
                            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: '6px', fontSize: 14 }} />
                            </Box>
                        }
                    >
                        <span dangerouslySetInnerHTML={{ __html: searchDescription }} />
                    </Alert>
                </Box>
            )}
            {!showFilters && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        variant='caption'
                        sx={{ background: theme.palette.searchbarbackground, p: 1, mr: 2 }}
                        component={Box}
                    >
                        <span dangerouslySetInnerHTML={{ __html: searchDescription }} />
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function PlatformFetchingStatus(props) {
    const { search } = props;
    const [moreInfo, setMoreInfo] = useState(false);

    if (!search) return null;

    const donePlatforms = search.donePlatforms || [];
    
    // Only show if platforms are still being fetched
    if (search.platforms.length === donePlatforms.length) return null;

    return (
        <Box sx={{ marginBottom: '12px' }}>
            {moreInfo && (
                <Alert
                    severity='info'
                    sx={{ textWrap: 'wrap', marginBottom: '3px', position: 'relative', marginBottom: '6px' }}
                    icon={<CircularProgress size={14} />}
                    action={
                        <Button 
                            size="small" 
                            color="primary" 
                            onClick={e => { 
                                e.stopPropagation(); 
                                setMoreInfo(false); 
                            }}
                            sx={{ fontSize: '11px', marginLeft: '12px', textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                            Less info
                        </Button>
                    }
                >
                    Fetching {search.mode.length === 2 ? 'online rates and company tariff' : search.mode.includes('rates') ? 'online rates' : 'company tariff'}:
                    <Box sx={{ marginTop: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {(search.platforms || []).map((platform, index) => {
                            const completed = donePlatforms.find(d => d.id === platform.id);
                            const errorsForPlatform = _(search.results).filter(e => !!e.error && e.platform === platform.id).value();
                            const receivedResults = _(search.results).filter(r => !r.error && r.platform === platform.id).value();
                            return (
                                <Box key={platform.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {errorsForPlatform.length > 0 ? (
                                        <FontAwesomeIcon icon={faCircleXmark} color="red" style={{ marginRight: 8, fontSize: 16 }} />
                                    ) : completed ? (
                                        <FontAwesomeIcon icon={faSquareCheck} style={{ marginRight: 8, fontSize: 16 }} />
                                    ) : (
                                        <FontAwesomeIcon icon={faSquare} style={{ marginRight: 8, fontSize: 16 }} />
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '13px'
                                        }}
                                    >
                                        {platform.name}
                                        {completed && errorsForPlatform.length === 0 && (
                                            <span style={{ fontWeight: 400, marginLeft: 6, color: receivedResults.length > 0 ? "#4caf50" : "#9e9e9e" }}>
                                                {receivedResults.length > 0 ? `(${receivedResults.length} received)` : "(0 received)"}
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Alert>
            )}
            {!moreInfo && (
                <Alert 
                    severity='info' 
                    sx={{ textWrap: 'wrap', marginBottom: '3px', display: 'flex', alignItems: 'center', fontSize: '13px', marginBottom: '6px' }}
                    icon={<CircularProgress size={14} />}
                    action={
                        <Button 
                            size="small" 
                            color="primary" 
                            onClick={e => { 
                                e.stopPropagation(); 
                                setMoreInfo(true); 
                            }}
                            sx={{ fontSize: '11px', marginLeft: '12px', textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                            More info
                        </Button>
                    }
                >
                    Fetching {search.mode.length === 2 ? 'online rates and company tariff' : search.mode.includes('rates') ? 'online rates' : 'company tariff'}. {donePlatforms.length} of {search.platforms.length} {pluralize('platform', (search.platforms || []).length)} completed.
                </Alert>
            )}
        </Box>
    );
}

function InnerRatesDetails(props) {
    const { rate, organization, setMinimizedSearch, searchLCLProduct, searchAirFreightProduct } = props;

    const transportationMethod = rate.transportationMethod;
    const products = (searchLCLProduct && rate.product?.type === 'LCL')
        ? [{ ...rate.product, calculations: searchLCLProduct.calculations, kgOrLbs: searchLCLProduct.kgOrLbs, cmOrInches: searchLCLProduct.cmOrInches }]
        : (searchAirFreightProduct && rate.product?.type === 'Air freight')
        ? [{ ...rate.product, calculations: searchAirFreightProduct.calculations, kgOrLbs: searchAirFreightProduct.kgOrLbs, cmOrInches: searchAirFreightProduct.cmOrInches }]
        : rate.product ? [ rate.product ] : null;
    return (
        <Box sx={{ width: '473px', padding: '12px', fontSize: '13px' }} >
            <RatesDetails
                organization={organization}
                form={rate.form || organization.forms[rate.formId]}
                rates={[rate]}
                rfq={{ products, transportationMethod }}
                opportunityProducts={products}
                onStartDragging={() => {
                    setMinimizedSearch(true);
                }}
                onEndDragging={() => {
                    setMinimizedSearch(false);
                }}
            />
        </Box>
    );
}

function FilterAndSort(props) {
    const { showFilters, filters, setFilters, sorting, setSorting, modeFilter, setModeFilter, hideSections, setHideSections, results, search } = props;

    const [ showingAnchor, setShowingAnchor] = useState(null);

    const theme = useTheme();

    useEffect(() => {
        const modeOptions = _.uniq(results?.map(r => r.type === 'tariff' ? 'tariff' : 'rates'));
        if (modeOptions.length > 0 && !modeOptions.includes(modeFilter)) {
            setModeFilter(modeOptions[0]);
        }
    }, [results]);

    if (!results || results.length === 0) return null;

    // Judge the filter row on the same rates the list renders: its count, and the section
    // options it offers, must not include rates the list drops for unusable validity.
    const shownResults = results.filter(r => !hasUnusableValidity(r));

    const nonErrorResults = shownResults.filter(r => !r.error);
    const sections = _.compact(_.uniq(shownResults.map(r => r.offer?.sections?.filter(s => {
        return s.offers[0].fields.some(f => f.type !== 'string' && _.values(f.values)[0].value > 0);
    }).map(s => s.title)).flat()));

    const modeOptions = ['rates', 'tariff'];

    // Calculate filtered result count
    const filteredCount = nonErrorResults.filter(r => {
        if ((r.type === 'tariff' ? 'tariff' : 'rates') !== modeFilter) return false;
        if (filters.length > 0 && !filters.every(f => f.func(r))) return false;
        return true;
    }).length;

    return (
        <Box sx={{ marginBottom: '12px' }}>
            {/* Filter Row with | separators - matching old structure */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0,
                    marginRight: '6px',
                    marginTop: '6px',
                    paddingTop: '6px',
                    paddingBottom: '8px',
                    borderRadius: 1.5,
                    boxShadow: theme => theme.shadows[1],
                    background: theme => theme.palette.background.paper,
                    minHeight: 42,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    overflow: 'auto'
                }}
                component="section"
                role="toolbar"
                aria-label="Search tools"
            >
                {/* Sort */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', px: 1.5,
                    borderRight: theme => `1px solid ${theme.palette.divider}`,
                    height: 40,
                    background: 'none',
                    minWidth: 0,
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <SortingPanel
                        sorting={sorting}
                        setSorting={setSorting}
                    />
                </Box>

                {/* Filter - Combined Mode, Product, Carrier */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', px: 1.5,
                    borderRight: theme => `1px solid ${theme.palette.divider}`,
                    height: 40,
                    background: 'none',
                    minWidth: 0,
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <FiltersPanel
                        results={results}
                        filters={filters}
                        setFilters={setFilters}
                        modeFilter={modeFilter}
                        setModeFilter={setModeFilter}
                    />
                </Box>

                {/* Result Count - in the empty space between Product and Showing */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', px: 1.5,
                    borderRight: theme => `1px solid ${theme.palette.divider}`,
                    height: 40,
                    background: 'none',
                    minWidth: 0,
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '11px',
                            color: theme.palette.text.secondary,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {filteredCount} {pluralize('result', filteredCount)}
                    </Typography>
                </Box>

                {/* Showing */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', px: 1.5,
                    minWidth: 0,
                    height: 40,
                    background: 'none',
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <Button
                        variant="text"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            fontSize: '11px !important',
                            px: 1.5,
                            minWidth: '0px',
                            borderRadius: 1,
                            whiteSpace: 'nowrap',
                            height: 30,
                            background: 'none'
                        }}
                        id="showing-button"
                        aria-controls="showing-menu"
                        aria-haspopup="true"
                        onClick={e => {
                            setShowingAnchor(e.currentTarget || e.target);
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        Showing
                        <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: '6px', fontSize: 11, opacity: 0.6 }} />
                    </Button>
                    <Menu
                        anchorEl={showingAnchor}
                        open={Boolean(showingAnchor)}
                        onClose={e => { setShowingAnchor(null); e?.stopPropagation && e.stopPropagation(); }}
                        slotProps={{ 
                            root: { sx: { zIndex: 1000000 }},
                            paper: {
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        fontSize: '12px !important',
                                    }
                                }
                            }
                        }}
                    >
                        {sections.map((type, index) => (
                            <MenuItem
                                key={type}
                                sx={{ pl: 0.5, fontSize: '12px !important' }}
                                dense
                                disableGutters
                                onClick={e => {
                                    setHideSections(_.xor(hideSections, [type]));
                                    e.stopPropagation();
                                }}
                            >
                                <Checkbox
                                    size='small'
                                    checked={!hideSections.includes(type)}
                                    icon={<FontAwesomeIcon icon={faSquare} size="sm" />}
                                    checkedIcon={<FontAwesomeIcon icon={faCheckSquare} size="sm" />}
                                    sx={{ p: '3px', mr: 1 }}
                                    tabIndex={-1}
                                />
                                <Typography variant="caption" sx={{ fontSize: '11px !important', flex: 1 }}>
                                    {type}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>

        </Box>
    );
}

export default SearchPane;

const accordiontheme = theme => {
    const t = _.cloneDeep(theme);
    t.components = t.components || {};
    t.components.MuiAccordionSummary = t.components.MuiAccordionSummary || {};
    t.components.MuiAccordionSummary.styleOverrides = t.components.MuiAccordionSummary.styleOverrides || {};
    t.components.MuiAccordionSummary.styleOverrides.content = t.components.MuiAccordionSummary.styleOverrides.content || {};
    t.components.MuiAccordionSummary.styleOverrides.content.margin = '0px !important';

    t.components.MuiAccordion = t.components.MuiAccordion || {};
    t.components.MuiAccordion.styleOverrides = t.components.MuiAccordion.styleOverrides || {};
    t.components.MuiAccordion.styleOverrides.root = t.components.MuiAccordion.styleOverrides.root || {};
    t.components.MuiAccordion.styleOverrides.root.background = theme.palette.panel1;
    return createTheme(t);
}

async function getDB({ orgAlias }) {
    return new Promise((res, rej) => {
        const request = indexedDB.open(`${orgAlias}-searchpane`, 1);
        request.onerror = (event) => {
            console.error('[SearchPane] Why didn\'t you allow my web app to use IndexedDB?!', event);
            rej(event.target.error);
        };
        request.onsuccess = (event) => {
            res(event.target.result);
        };
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            try {
                db.createObjectStore('searches', { keyPath: 'created' });
            } catch (e) {
                console.warn('[SearchPane] Object stores might already exist:', e);
            }
        };
    });
}

async function getPort({ unlocode, accesstoken, source, organization, transportationMethod }) {
    console.log('[SidePane.rates - getPort] Called with:', { unlocode, accesstoken, source, organizationId: organization?.id, transportationMethod });
    const isCargoFive = _.get(organization, 'integrations.cargofive.enabled', false) && 
                        _.get(organization, 'integrations.cargofive.useForRates', false);

    if (isCargoFive && transportationMethod === 'sea') {
        console.log('[SidePane.rates - getPort] CargoFive enabled and transportationMethod is sea.');
        const searchTerm = source?.type === 'port' ? source.id : source.id || source.text;
        const baseParams = [
            `search=${encodeURIComponent(searchTerm)}`,
            `accesstoken=${accesstoken}`,
            `organizationId=${organization.id}`,
        ];
        try {
            console.log('[SidePane.rates - getPort] Making CargoFive API calls with params:', baseParams);
            const [portsResponse, unlocodesResponse] = await Promise.all([
                axios.get(`/api/cargofive/places?${baseParams.join('&')}&place_type_id=1`),
                axios.get(`/api/cargofive/places?${baseParams.join('&')}&place_type_id=2`)
            ]);
            const port = portsResponse.data.data;
            const unlocodeResult = unlocodesResponse.data.data;
            console.log('[SidePane.rates - getPort] Received portsResponse:', port);
            console.log('[SidePane.rates - getPort] Received unlocodesResponse:', unlocodeResult);

            const mapped = [...port, ...unlocodeResult].map(p => ({
                type: p.place_type_id === 1 ? 'port' : 'unlocode',
                id: p.code,
                text: p.display_name || p.name || p.code,
                countryCode: p.api_variations?.one?.countryCode || p.api_variations?.oocl?.ctryCode || (p.code || '').substring(0, 2) || '',
                city: p.name || '',
                externalIds: {
                    ...(p.id && { cargofiveId: String(p.id) }),
                    ...(p.uuid && { cargofiveUuid: String(p.uuid) }),
                    ...(p.code && { code: String(p.code) })
                }
            }));
            console.log('[SidePane.rates - getPort] Mapped CargoFive places:', mapped);

            const first = _.first(mapped);
            if (!first) {
                console.warn('[SidePane.rates - getPort] No matching CargoFive ports/unlocodes found for searchTerm:', searchTerm);
            } else {
                console.log('[SidePane.rates - getPort] Returning first matching port/unlocode:', first);
            }
            return first;
        } catch (error) {
            console.error('[SidePane.rates - getPort] Error querying CargoFive places API:', error, error?.response?.data);
            return null;
        }
    }
    console.log('[SidePane.rates - getPort] Not CargoFive mode or not sea. Returning normalized source.');
    if (!source) {
        console.warn('[SidePane.rates - getPort] Source is null or undefined.');
        return null;
    }
    return source;
}